// SPDX-License-Identifier: AGPL-3.0-or-later

import {Routes} from '@app/app/Routes';
import {VOICE_CHANNEL_DESCRIPTOR} from '@app/features/i18n/utils/CommonMessageDescriptors';
import {Platform} from '@app/features/platform/types/Platform';
import type {SearchableSettingDescriptor} from '@app/features/user/components/settings_utils/search_index/SearchIndexTypes';
import {
	CHAT_INPUT_DESCRIPTOR,
	CONFIRM_VOICE_JOIN_DESCRIPTOR,
	ENABLE_FAVORITES_DESCRIPTOR,
	JOIN_VOICE_DESCRIPTOR,
	KEEP_NEKO_STILL_DESCRIPTOR,
	KEYBOARD_HINTS_DESCRIPTOR,
	NEKO_DESCRIPTOR,
	SHORTCUT_BADGES_DESCRIPTOR,
	SHOW_DOWNLOAD_BUTTON_DESCRIPTOR,
	SHOW_HELP_CENTER_BUTTON_DESCRIPTOR,
	SHOW_NEKO_DESCRIPTOR,
	TWO_CLICKS_DESCRIPTOR,
	VOICE_CHANNEL_CONFIRMATION_DESCRIPTOR,
} from '@app/features/user/components/settings_utils/section_registry/SharedDescriptors';
import {CONFIRM_BEFORE_JOINING_VOICE_CHANNELS_DESCRIPTOR} from '@app/features/user/utils/UserMessageDescriptors';
import {msg} from '@lingui/core/macro';

const VOICE_CHANNEL_JOIN_BEHAVIOR_DESCRIPTOR = msg({
	message: 'Voice channel join behavior',
	comment: 'Settings search entry label. Names the settings search entry in the settings UI.',
});

const COMMUNITY_VOICE_DESCRIPTOR = msg({
	message: 'Community voice',
	comment: 'Settings search synonym. Used to match this term when the user types it in the settings search bar.',
});

const CHOOSE_WHETHER_JOINING_A_COMMUNITY_VOICE_CHANNEL_ASKS_DESCRIPTOR = msg({
	message: 'Confirmation or double-click for community voice joins',
	comment: 'Settings search entry description. One-line summary of what the settings search entry controls.',
});
const HIDE_KEYBOARD_HINTS_DESCRIPTOR = msg({
	message: 'Hide keyboard hints',
	comment: 'Settings search synonym. Used to match this term when the user types it in the settings search bar.',
});
const TOOLTIP_SHORTCUTS_DESCRIPTOR = msg({
	message: 'Tooltip keybinds',
	comment: 'Settings search synonym. Used to match this term when the user types it in the settings search bar.',
});

const CHOOSE_WHETHER_TOOLTIPS_SHOW_KEYBOARD_SHORTCUT_HINTS_DESCRIPTOR = msg({
	message: 'Keybind hints in tooltips',
	comment: 'Settings search entry description. One-line summary of what the setting controls.',
});
const SHOW_OR_HIDE_NEKO_THAT_CHASES_THE_CURSOR_DESCRIPTOR = msg({
	message: 'Neko cat that chases your cursor',
	comment: 'Settings search entry description. One-line summary of what the setting controls.',
});
const KEEP_NEKO_FROM_CHASING_THE_CURSOR_DESCRIPTOR = msg({
	message: 'Stop Neko from chasing your cursor while keeping it draggable and interactive.',
	comment: 'Settings search entry description. One-line summary of what the setting controls.',
});
const STILL_NEKO_DESCRIPTOR = msg({
	message: 'Still Neko',
	comment: 'Settings search synonym. Used to match this term when the user types it in the settings search bar.',
});
const STATIC_NEKO_DESCRIPTOR = msg({
	message: 'Static Neko',
	comment: 'Settings search synonym. Used to match this term when the user types it in the settings search bar.',
});
const FAVORITE_CHANNELS_DESCRIPTOR = msg({
	message: 'Favorite channels',
	comment: 'Settings search synonym. Used to match this term when the user types it in the settings search bar.',
});
const STARRED_CHANNELS_DESCRIPTOR = msg({
	message: 'Starred channels',
	comment: 'Settings search synonym. Used to match this term when the user types it in the settings search bar.',
});
const CHOOSE_WHETHER_FAVORITES_ARE_VISIBLE_THROUGHOUT_THE_APP_DESCRIPTOR = msg({
	message: 'Show favorites throughout the app',
	comment: 'Settings search entry description. One-line summary of what the setting controls.',
});
const HELP_BUTTON_DESCRIPTOR = msg({
	message: 'Help button',
	comment: 'Settings search synonym. Used to match this term when the user types it in the settings search bar.',
});
const DOWNLOAD_APP_DESCRIPTOR = msg({
	message: 'Download app',
	comment: 'Settings search synonym. Used to match this term when the user types it in the settings search bar.',
});
const HELP_CENTER_BUTTON_IN_THE_SIDEBAR_DESCRIPTOR = msg({
	message: 'Help center button in the sidebar',
	comment: 'Settings search entry description. One-line summary of what the setting controls.',
});
const DOWNLOAD_BUTTON_IN_THE_SIDEBAR_DESCRIPTOR = msg({
	message: 'Download app button in the sidebar',
	comment: 'Settings search entry description. One-line summary of what the setting controls.',
});
const AUTO_PEEK_HIDDEN_SIDEBARS_DESCRIPTOR = msg({
	message: 'Auto-peek hidden sidebars',
	comment: 'Settings section label in Advanced Settings for edge hover peek preference.',
});
const AUTO_PEEK_HIDDEN_SIDEBARS_DESCRIPTION_DESCRIPTOR = msg({
	message: 'Hover near window edges to temporarily reveal collapsed panels.',
	comment: 'Settings section description in Advanced Settings for edge hover peek preference.',
});
const TASKBAR_DESCRIPTOR = msg({
	message: 'Taskbar',
	comment: 'Settings search synonym.',
});
const AUTO_HIDE_DESCRIPTOR = msg({
	message: 'Auto-hide',
	comment: 'Settings search synonym.',
});
const PEEK_DESCRIPTOR = msg({
	message: 'Peek',
	comment: 'Settings search synonym.',
});
export const appearanceIndex: Array<SearchableSettingDescriptor> = [
	{
		id: 'appearance-show-neko',
		tabType: 'appearance',
		sectionId: 'interface',
		label: SHOW_NEKO_DESCRIPTOR,
		keywords: [NEKO_DESCRIPTOR, CHAT_INPUT_DESCRIPTOR],
		description: SHOW_OR_HIDE_NEKO_THAT_CHASES_THE_CURSOR_DESCRIPTOR,
		audience: 'advanced',
		tags: ['appearance', 'chat'],
	},
	{
		id: 'appearance-keep-neko-still',
		tabType: 'appearance',
		sectionId: 'interface',
		label: KEEP_NEKO_STILL_DESCRIPTOR,
		keywords: [NEKO_DESCRIPTOR, STILL_NEKO_DESCRIPTOR, STATIC_NEKO_DESCRIPTOR],
		description: KEEP_NEKO_FROM_CHASING_THE_CURSOR_DESCRIPTOR,
		audience: 'advanced',
		tags: ['appearance', 'chat'],
	},
	{
		id: 'appearance-hide-keyboard-hints',
		tabType: 'appearance',
		sectionId: 'interface',
		label: KEYBOARD_HINTS_DESCRIPTOR,
		keywords: [HIDE_KEYBOARD_HINTS_DESCRIPTOR, TOOLTIP_SHORTCUTS_DESCRIPTOR, SHORTCUT_BADGES_DESCRIPTOR],
		description: CHOOSE_WHETHER_TOOLTIPS_SHOW_KEYBOARD_SHORTCUT_HINTS_DESCRIPTOR,
		audience: 'advanced',
		tags: ['appearance'],
	},
	{
		id: 'appearance-enable-favorites',
		tabType: 'appearance',
		sectionId: 'channel-list',
		label: ENABLE_FAVORITES_DESCRIPTOR,
		keywords: [FAVORITE_CHANNELS_DESCRIPTOR, STARRED_CHANNELS_DESCRIPTOR],
		description: CHOOSE_WHETHER_FAVORITES_ARE_VISIBLE_THROUGHOUT_THE_APP_DESCRIPTOR,
		audience: 'advanced',
		tags: ['appearance'],
	},
	{
		id: 'appearance-show-help-center-button',
		tabType: 'appearance',
		sectionId: 'interface',
		label: SHOW_HELP_CENTER_BUTTON_DESCRIPTOR,
		keywords: [HELP_BUTTON_DESCRIPTOR],
		description: HELP_CENTER_BUTTON_IN_THE_SIDEBAR_DESCRIPTOR,
		audience: 'advanced',
		tags: ['appearance'],
		isVisible: () => Routes.help() != null,
	},
	{
		id: 'appearance-show-download-button',
		tabType: 'appearance',
		sectionId: 'interface',
		label: SHOW_DOWNLOAD_BUTTON_DESCRIPTOR,
		keywords: [DOWNLOAD_APP_DESCRIPTOR],
		description: DOWNLOAD_BUTTON_IN_THE_SIDEBAR_DESCRIPTOR,
		audience: 'advanced',
		tags: ['appearance'],
		isVisible: () => !Platform.isElectron && !Platform.isPWA,
	},
	{
		id: 'appearance-voice-channel-join-behavior',
		tabType: 'appearance',
		sectionId: 'interface',
		label: VOICE_CHANNEL_JOIN_BEHAVIOR_DESCRIPTOR,
		keywords: [
			CONFIRM_BEFORE_JOINING_VOICE_CHANNELS_DESCRIPTOR,
			VOICE_CHANNEL_CONFIRMATION_DESCRIPTOR,
			CONFIRM_VOICE_JOIN_DESCRIPTOR,
			JOIN_VOICE_DESCRIPTOR,
			VOICE_CHANNEL_DESCRIPTOR,
			COMMUNITY_VOICE_DESCRIPTOR,
			TWO_CLICKS_DESCRIPTOR,
		],
		description: CHOOSE_WHETHER_JOINING_A_COMMUNITY_VOICE_CHANNEL_ASKS_DESCRIPTOR,
		audience: 'advanced',
		tags: ['appearance', 'voice'],
	},
	{
		id: 'appearance-edge-hover-peek',
		tabType: 'appearance',
		sectionId: 'interface',
		label: AUTO_PEEK_HIDDEN_SIDEBARS_DESCRIPTOR,
		keywords: [TASKBAR_DESCRIPTOR, AUTO_HIDE_DESCRIPTOR, PEEK_DESCRIPTOR],
		description: AUTO_PEEK_HIDDEN_SIDEBARS_DESCRIPTION_DESCRIPTOR,
		audience: 'advanced',
		tags: ['appearance'],
	},
];
